import * as THREE from 'three';
import { convertMmToM } from '@puppycad/cad-core';
import type {
  ExtrudeHandleRole,
  ExtrudeHandleSide,
} from './extrude-direction-drag-state';
import {
  ExtrudeHandleGizmoObject,
  type ExtrudeHandleGizmoParts,
} from '@/core/extrude/object/ExtrudeHandleGizmoObject';
import {
  ExtrudeHandlePartGroup,
  type ExtrudeGizmoPartKind,
} from '@/core/extrude/object/ExtrudeHandlePartGroup';

//------ 拉伸手柄毫米级适配 开始------
/**
 * 拉伸手柄默认留白，单位为米。
 *
 * 说明：
 * 1. 场景底层单位仍为米，0.006 表示 6 mm；
 * 2. 该值只控制手柄超过真实拉伸深度的最小视觉余量；
 * 3. 避免旧的 1 m 留白在毫米级零件中撑爆视口。
 */
const EXTRUDE_HANDLE_MIN_MARGIN_M = convertMmToM(6);

/**
 * 拉伸手柄初始可视长度，单位为米。
 *
 * 说明：
 * 1. 手柄创建后会立刻按当前状态刷新；
 * 2. 初始值仍需要保持毫米级，避免刷新前短暂出现米级巨大对象；
 * 3. 该长度对应 16 mm，覆盖 10 mm 默认深度与最小视觉留白。
 */
const EXTRUDE_HANDLE_INITIAL_VISIBLE_LENGTH_M = convertMmToM(16);

/**
 * 拉伸手柄初始轴半径，单位为米。
 */
const EXTRUDE_HANDLE_INITIAL_SHAFT_RADIUS_M = convertMmToM(1);

/**
 * 拉伸手柄初始箭头长度，单位为米。
 */
const EXTRUDE_HANDLE_INITIAL_HEAD_SIZE_M = convertMmToM(6);
//------ 拉伸手柄毫米级适配 结束------

export type { ExtrudeGizmoPartKind } from '@/core/extrude/object/ExtrudeHandlePartGroup';

/**
 * 轮廓几何抽象。
 */
export type ContourGeometryLike = {
  /** 外环点列。 */
  outer: Array<[number, number, number]>;
  /** 孔洞点列。 */
  holes?: Array<Array<[number, number, number]>>;
};

/**
 * 轴向拖拽深度输入。
 */
export type AxisDragDepthInput = {
  /** 轴原点。 */
  axisOrigin: THREE.Vector3;
  /** 轴方向。 */
  axisDirection: THREE.Vector3;
  /** 拖拽起点。 */
  startPoint: THREE.Vector3;
  /** 当前点。 */
  currentPoint: THREE.Vector3;
  /** 起始深度。 */
  startDepth: number;
};

/**
 * 手柄构造参数。
 */
export type ExtrudeHandleGizmoOptions = {
  /** 主颜色。 */
  color?: number;
};

/**
 * 手柄长度计算参数。
 */
export type ExtrudeHandleLengthOptions = {
  /** 最小留白。 */
  minMargin?: number;
  /** 留白比例。 */
  marginRatio?: number;
};

/**
 * 手柄视觉更新参数。
 */
export type ExtrudeHandleVisualOptions = {
  /** 正法向一侧可视长度。 */
  positiveLength: number;
  /** 反法向一侧可视长度。 */
  negativeLength: number;
  /** 正法向一侧角色。 */
  positiveRole: ExtrudeHandleRole | null;
  /** 反法向一侧角色。 */
  negativeRole: ExtrudeHandleRole | null;
  /** 轴半径。 */
  shaftRadius: number;
  /** 箭头尺寸。 */
  headSize: number;
};

/**
 * 归一化轴方向。
 *
 * @param axisDirection 轴方向。
 * @returns 单位向量。
 */
function normalizeAxis(axisDirection: THREE.Vector3): THREE.Vector3 {
  if (axisDirection.lengthSq() <= 1e-12) {
    return new THREE.Vector3(0, 0, 1);
  }
  return axisDirection.clone().normalize();
}

/**
 * 构建手柄材质。
 *
 * @param color 主颜色。
 * @returns 材质实例。
 */
function buildConeMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    metalness: 0.42,
    roughness: 0.58,
    emissive: 0x20242a,
    emissiveIntensity: 0.02,
    depthTest: false,
    depthWrite: false,
    transparent: true,
    opacity: 0.92,
  });
}

/**
 * 创建单圆锥手柄组。
 *
 * @param color 主颜色。
 * @param side 手柄侧别。
 * @param role 手柄角色。
 * @param kind 部件类型。
 * @returns 手柄分组对象。
 */
function buildSingleHeadGroup(
  color: number,
  side: ExtrudeHandleSide,
  role: ExtrudeHandleRole,
  kind: Extract<ExtrudeGizmoPartKind, 'positivePrimaryHead' | 'negativePrimaryHead'>,
): ExtrudeHandlePartGroup {
  const group = new ExtrudeHandlePartGroup({
    partKind: kind,
    side,
    role,
  });
  const cone = new THREE.Mesh(new THREE.ConeGeometry(1, 1, 16), buildConeMaterial(color));
  cone.rotation.x = side === 'positive' ? Math.PI * 0.5 : -Math.PI * 0.5;
  group.add(cone);
  group.renderOrder = 81;
  return group;
}

/**
 * 创建双圆锥手柄组。
 *
 * @param color 主颜色。
 * @param side 手柄侧别。
 * @param kind 部件类型。
 * @returns 手柄分组对象。
 */
function buildSecondaryHeadGroup(
  color: number,
  side: ExtrudeHandleSide,
  kind: Extract<ExtrudeGizmoPartKind, 'positiveSecondaryHead' | 'negativeSecondaryHead'>,
): ExtrudeHandlePartGroup {
  const group = new ExtrudeHandlePartGroup({
    partKind: kind,
    side,
    role: 'secondary',
  });
  const outwardCone = new THREE.Mesh(new THREE.ConeGeometry(1, 1, 16), buildConeMaterial(color));
  const inwardCone = new THREE.Mesh(new THREE.ConeGeometry(1, 1, 16), buildConeMaterial(color));
  outwardCone.rotation.x = side === 'positive' ? Math.PI * 0.5 : -Math.PI * 0.5;
  inwardCone.rotation.x = side === 'positive' ? -Math.PI * 0.5 : Math.PI * 0.5;
  group.add(outwardCone);
  group.add(inwardCone);
  group.renderOrder = 81;
  return group;
}

/**
 * 计算多个轮廓的综合中心。
 *
 * @param contours 轮廓集合。
 * @returns 综合中心。
 */
export function computeSelectedContoursCenter(contours: ContourGeometryLike[]): THREE.Vector3 {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;

  contours.forEach((contour) => {
    contour.outer.forEach((point) => {
      minX = Math.min(minX, point[0]);
      minY = Math.min(minY, point[1]);
      minZ = Math.min(minZ, point[2]);
      maxX = Math.max(maxX, point[0]);
      maxY = Math.max(maxY, point[1]);
      maxZ = Math.max(maxZ, point[2]);
    });
  });

  if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
    return new THREE.Vector3();
  }

  return new THREE.Vector3(
    (minX + maxX) * 0.5,
    (minY + maxY) * 0.5,
    (minZ + maxZ) * 0.5,
  );
}

/**
 * 计算点在轴上的投影深度。
 *
 * @param axisOrigin 轴原点。
 * @param axisDirection 轴方向。
 * @param point 目标点。
 * @returns 投影深度。
 */
export function computeAxisProjectedDepth(
  axisOrigin: THREE.Vector3,
  axisDirection: THREE.Vector3,
  point: THREE.Vector3,
): number {
  const axis = normalizeAxis(axisDirection);
  return point.clone().sub(axisOrigin).dot(axis);
}

/**
 * 根据轴向拖拽计算深度。
 *
 * @param input 输入参数。
 * @returns 新深度。
 */
export function computeDepthFromAxisDrag(input: AxisDragDepthInput): number {
  const startProjected = computeAxisProjectedDepth(input.axisOrigin, input.axisDirection, input.startPoint);
  const currentProjected = computeAxisProjectedDepth(input.axisOrigin, input.axisDirection, input.currentPoint);
  return input.startDepth + (currentProjected - startProjected);
}

/**
 * 计算单侧手柄长度。
 *
 * @param depth 深度。
 * @param options 配置。
 * @returns 可视长度。
 */
export function computeExtrudeHandleOneSideLength(
  depth: number,
  options: ExtrudeHandleLengthOptions = {},
): number {
  const absDepth = Math.max(0, Math.abs(depth));
  const minMargin = Math.max(1e-4, options.minMargin ?? EXTRUDE_HANDLE_MIN_MARGIN_M);
  const marginRatio = Math.max(0, options.marginRatio ?? 0.15);
  const margin = Math.max(minMargin, absDepth * marginRatio);
  return absDepth + margin;
}

/**
 * 计算射线与轴线的最近点。
 *
 * @param ray 射线。
 * @param axisOrigin 轴原点。
 * @param axisDirection 轴方向。
 * @returns 轴线上最近点。
 */
export function closestPointOnAxisFromRay(
  ray: THREE.Ray,
  axisOrigin: THREE.Vector3,
  axisDirection: THREE.Vector3,
): THREE.Vector3 {
  const axis = normalizeAxis(axisDirection);
  const rayDirection = ray.direction.clone().normalize();
  const delta = ray.origin.clone().sub(axisOrigin);
  const a = rayDirection.dot(rayDirection);
  const b = rayDirection.dot(axis);
  const c = axis.dot(axis);
  const d = rayDirection.dot(delta);
  const e = axis.dot(delta);
  const denominator = a * c - b * b;

  const axisParam = Math.abs(denominator) > 1e-9
    ? (a * e - b * d) / denominator
    : e / c;

  return axisOrigin.clone().addScaledVector(axis, axisParam);
}

/**
 * 获取手柄部件集合。
 *
 * @param gizmo 手柄根对象。
 * @returns 部件集合。
 */
function getGizmoParts(gizmo: THREE.Object3D): ExtrudeHandleGizmoParts | null {
  if (!(gizmo instanceof ExtrudeHandleGizmoObject)) {
    return null;
  }
  return gizmo.parts;
}

/**
 * 解析命中对象对应的手柄部件类型。
 *
 * @param object 命中对象。
 * @returns 部件类型；无效时返回 unknown。
 */
export function resolveExtrudeGizmoPartKindFromObject(
  object: THREE.Object3D | null,
): ExtrudeGizmoPartKind | 'unknown' {
  let current: THREE.Object3D | null = object;
  while (current) {
    if (current instanceof ExtrudeHandlePartGroup) {
      return current.visible ? current.partKind : 'unknown';
    }
    current = current.parent;
  }
  return 'unknown';
}

/**
 * 收集当前可交互的手柄对象。
 *
 * @param gizmo 手柄根对象。
 * @returns 可交互对象列表。
 */
export function collectExtrudeHandlePickTargets(gizmo: THREE.Object3D): THREE.Object3D[] {
  const parts = getGizmoParts(gizmo);
  if (!parts) {
    return [];
  }

  const targets: THREE.Object3D[] = [parts.shaft];
  if (parts.positivePrimaryHead.visible) {
    targets.push(parts.positivePrimaryHead);
  }
  if (parts.negativePrimaryHead.visible) {
    targets.push(parts.negativePrimaryHead);
  }
  if (parts.positiveSecondaryHead.visible) {
    targets.push(parts.positiveSecondaryHead);
  }
  if (parts.negativeSecondaryHead.visible) {
    targets.push(parts.negativeSecondaryHead);
  }
  return targets;
}

/**
 * 为头部组统一设置尺寸。
 *
 * @param group 手柄组。
 * @param headRadius 圆锥半径。
 * @param headLength 圆锥长度。
 */
function scaleHeadGroup(
  group: ExtrudeHandlePartGroup,
  headRadius: number,
  headLength: number,
): void {
  group.children.forEach((child, index) => {
    if (!(child instanceof THREE.Mesh)) {
      return;
    }
    child.scale.set(headRadius, headLength, headRadius);
    if (group.role === 'secondary') {
      const side = group.side as ExtrudeHandleSide;
      const isOutwardCone = index === 0;
      if (side === 'positive') {
        child.position.z = isOutwardCone ? headLength * 0.5 : -headLength * 0.5;
      } else {
        child.position.z = isOutwardCone ? -headLength * 0.5 : headLength * 0.5;
      }
      return;
    }
    child.position.z = group.side === 'positive'
      ? headLength * 0.5
      : -headLength * 0.5;
  });
}

/**
 * 创建拉伸手柄对象。
 *
 * @param options 构造参数。
 * @returns 手柄对象。
 */
export function buildExtrudeHandleGizmo(options: ExtrudeHandleGizmoOptions = {}): THREE.Group {
  const color = options.color ?? 0xb8bfc8;
  const shaft = new ExtrudeHandlePartGroup({
    partKind: 'shaft',
    side: null,
    role: null,
  });
  const shaftMesh = new THREE.Mesh(
    new THREE.CylinderGeometry(1, 1, 1, 16),
    buildConeMaterial(color),
  );
  shaftMesh.rotation.x = Math.PI * 0.5;
  shaftMesh.renderOrder = 80;
  shaft.add(shaftMesh);

  const parts: ExtrudeHandleGizmoParts = {
    shaft,
    positivePrimaryHead: buildSingleHeadGroup(color, 'positive', 'primary', 'positivePrimaryHead'),
    negativePrimaryHead: buildSingleHeadGroup(color, 'negative', 'primary', 'negativePrimaryHead'),
    positiveSecondaryHead: buildSecondaryHeadGroup(color, 'positive', 'positiveSecondaryHead'),
    negativeSecondaryHead: buildSecondaryHeadGroup(color, 'negative', 'negativeSecondaryHead'),
  };
  const group = new ExtrudeHandleGizmoObject(parts);
  group.name = '__extrude_handle_gizmo__';
  group.add(parts.shaft);
  group.add(parts.positivePrimaryHead);
  group.add(parts.negativePrimaryHead);
  group.add(parts.positiveSecondaryHead);
  group.add(parts.negativeSecondaryHead);

  updateExtrudeHandleGizmoVisual(group, {
    positiveLength: EXTRUDE_HANDLE_INITIAL_VISIBLE_LENGTH_M,
    negativeLength: 0,
    positiveRole: 'primary',
    negativeRole: null,
    shaftRadius: EXTRUDE_HANDLE_INITIAL_SHAFT_RADIUS_M,
    headSize: EXTRUDE_HANDLE_INITIAL_HEAD_SIZE_M,
  });

  return group;
}

/**
 * 更新手柄视觉长度与朝向。
 *
 * @param gizmo 手柄对象。
 * @param options 更新参数。
 */
export function updateExtrudeHandleGizmoVisual(
  gizmo: THREE.Object3D,
  options: ExtrudeHandleVisualOptions,
): void {
  const parts = getGizmoParts(gizmo);
  if (!parts) {
    return;
  }

  const positiveLength = Math.max(0, options.positiveLength);
  const negativeLength = Math.max(0, options.negativeLength);
  const shaftRadius = Math.max(1e-4, options.shaftRadius);
  const headSize = Math.max(1e-4, options.headSize);
  const headRadius = Math.max(1e-4, headSize * 0.6);
  const shaftMesh = parts.shaft.children[0];

  if (shaftMesh instanceof THREE.Mesh) {
    shaftMesh.scale.set(
      shaftRadius,
      Math.max(1e-4, positiveLength + negativeLength),
      shaftRadius,
    );
    shaftMesh.position.z = (positiveLength - negativeLength) * 0.5;
  }

  scaleHeadGroup(parts.positivePrimaryHead, headRadius, headSize);
  scaleHeadGroup(parts.negativePrimaryHead, headRadius, headSize);
  scaleHeadGroup(parts.positiveSecondaryHead, headRadius, headSize);
  scaleHeadGroup(parts.negativeSecondaryHead, headRadius, headSize);

  // 尚未取得有效预览时仍在起点显示方向箭头，不能把只读终止解释成没有方向。
  parts.positivePrimaryHead.visible = options.positiveRole === 'primary';
  parts.negativePrimaryHead.visible = options.negativeRole === 'primary';
  parts.positiveSecondaryHead.visible = options.positiveRole === 'secondary';
  parts.negativeSecondaryHead.visible = options.negativeRole === 'secondary';

  parts.positivePrimaryHead.position.z = positiveLength;
  parts.positiveSecondaryHead.position.z = positiveLength;
  parts.negativePrimaryHead.position.z = -negativeLength;
  parts.negativeSecondaryHead.position.z = -negativeLength;
}

/**
 * 设置手柄世界朝向。
 *
 * @param gizmo 手柄对象。
 * @param origin 原点。
 * @param axisDirection 轴方向。
 */
export function orientExtrudeHandleGizmo(
  gizmo: THREE.Object3D,
  origin: THREE.Vector3,
  axisDirection: THREE.Vector3,
): void {
  const axis = normalizeAxis(axisDirection);
  gizmo.position.copy(origin);
  gizmo.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), axis);
}

/**
 * 将屏幕像素尺寸换算为世界尺寸。
 *
 * @param camera 相机。
 * @param container 容器。
 * @param worldPoint 参考世界点。
 * @param pixelSize 像素尺寸。
 * @returns 世界尺寸。
 */
export function computeWorldSizeFromPixels(
  camera: THREE.Camera,
  container: HTMLElement,
  worldPoint: THREE.Vector3,
  pixelSize: number,
): number {
  const viewportHeight = Math.max(1, container.clientHeight);
  if (camera instanceof THREE.PerspectiveCamera) {
    const distance = Math.max(1e-6, camera.position.distanceTo(worldPoint));
    const fovRad = THREE.MathUtils.degToRad(camera.fov);
    const worldHeight = 2 * Math.tan(fovRad * 0.5) * distance;
    return (worldHeight / viewportHeight) * pixelSize;
  }
  if (camera instanceof THREE.OrthographicCamera) {
    const worldHeight = (camera.top - camera.bottom) / Math.max(1e-6, camera.zoom);
    return (worldHeight / viewportHeight) * pixelSize;
  }
  return pixelSize * 0.01;
}
